import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import analytics from '../../../../modules/analytics';
import useServiceInteractionCounter from '../../../../hooks/useServiceInteractionCounter';

import {
  ServicePropType,
  PromotionPropType,
  ServiceGroupPropType,
} from '../ServiceList/ServiceListPropTypes';
import Button from '../../../ui/Button';
import { getBookingParams } from '../../../../modules/consumer/Booking';
import { useLinkTo, useInTest } from '../../../../hooks';
import { callProviderWithPhone } from '../../../../modules/provider/callProvider';
import { SORTED_SERVICE_TEST_FLAG } from '../ServiceList/constants';
import {
  PROFILE_EDIT_ROUTE,
  PROFILE_MESSAGE,
  PRO_EDIT_SERVICE,
} from '../../../../route-names';

/**
 * Renders a booking button, which is responsible for tracking clicks and triggering the subsequent
 * navigations or actions. This is also responsible for determining which action to take when
 * clicked.
 */
const BookButton = ({
  isEditMode,
  isOnlineBookable,
  service,
  providerId,
  providerPhone,
  disabled,
  promotion,
  index,
  group,
  bookText,
  size,
  style,
  additionalTracking,
  onClick,
  testID,
  highlightedServiceId,
}) => {
  const inSortedServicesTest = useInTest(SORTED_SERVICE_TEST_FLAG);
  const linkTo = useLinkTo();
  const routeName = useSelector(state => state.route.name);
  const stateParams = useSelector(state => state.route.params);
  const promoted = !!promotion;
  let link = null;
  let linkParams = null;
  let ctaText = 'Request';
  const serviceId = service.id;
  const serviceIds = service.idList || [];
  const countInteraction = useServiceInteractionCounter();

  let bookRoute;

  if (typeof routeName === 'string' && routeName.includes(PROFILE_EDIT_ROUTE)) {
    bookRoute = `${PROFILE_EDIT_ROUTE}.book`;
  } else {
    bookRoute = 'profile.book';
  }

  let clickTracking;
  const trackingParams = {
    provider_id: providerId,
    service_id: serviceId || serviceIds.join(','),
  };
  let handleClick = () => {
    countInteraction(providerId, service.id);
    if (disabled) {
      return;
    }

    if (!isOnlineBookable) {
      // If pro account is disabled, or pro does not have a phone number listed, show the
      // message popup. Otherwise, call the pro.
      if (disabled || !callProviderWithPhone(providerId, providerPhone, 'profile')) {
        linkTo(PROFILE_MESSAGE, { providerId });
      }
    } else {
      window.scrollTo(0, 0);
    }

    let groupName = 'uncategorized';

    if (group && group.name) {
      groupName = group.name;
    } else if (service && service.servicegroup_name) {
      // try to get the groupName out of the service if the group doesn't exist
      // this is true in the case of promoted services
      groupName = service.servicegroup_name;
    }

    // Tracking
    const serviceTrackingParams = {
      ...stateParams,
      service_id: serviceId,
      service_ids: serviceIds,
      provider_id: providerId,
      service_name: service.name,
      profile_type: 'provider',
      is_previous_booking: service.isPreviousBooking,
      online_bookable: isOnlineBookable,

      /**
       * These tell us the location of the button:
       *  - top service swiper
       *  - promoted section
       *  - service info modal
       */
      is_top_service: service.isTopService,
      promoted,
      group: groupName,
      /** Have the services been sorted to move the highlighted service to the top */
      viewing_sorted_services: highlightedServiceId && inSortedServicesTest,
      highlighted_service_id: highlightedServiceId,
    };
    if (typeof group?.index === 'number'
      && typeof index === 'number') {
      // Are these relevant anymore?
      // Tell us the location of the button in the service list
      serviceTrackingParams.service_index_in_group = index + 1;
      serviceTrackingParams.service_index = group.startingOverallIndex + index + 1;
      serviceTrackingParams.group_index = promoted ? 0 : (group.index + 1);
    }

    serviceTrackingParams.provider_id_viewed = providerId;

    // NOTE: If this event name changes, notify team Flywheel (FLY-2854) and data team (DT-1004)
    analytics.track('client_proprofile_book_click', {
      ...getBookingParams(stateParams),
      ...serviceTrackingParams,
      ...additionalTracking,
    });

    if (typeof onClick === 'function') {
      onClick();
    }
  };

  if (isOnlineBookable) {
    ctaText = bookText;
    link = bookRoute;
    linkParams = {
      ...stateParams,
      allServices: serviceIds ? serviceIds.join(',') : undefined,
      providerId,
      proId: providerId,
      serviceId,
    };
  }

  if (isEditMode) {
    clickTracking = {
      name: 'provider_profile_edit_click',
      params: trackingParams,
    };
    ctaText = 'Edit';
    link = PRO_EDIT_SERVICE;
    linkParams = {
      ...stateParams,
      providerId,
      serviceId: service.id,
      editService: true,
      source: 'profile',
    };

    handleClick = null;
  }

  return (
    <Button
      onClick={handleClick}
      link={link}
      linkParams={linkParams}
      disabled={disabled}
      clickTracking={clickTracking}
      testID={testID}
      size={size}
      style={style}
      appearance="primary-neue"
    >
      {ctaText}
    </Button>
  );
};

BookButton.propTypes = {
  /** True if edit mode is enabled */
  isEditMode: PropTypes.bool,
  /** True if the service is bookable online */
  isOnlineBookable: PropTypes.bool,
  /** The service to book */
  service: ServicePropType.isRequired,
  /** The provider associated with the service */
  providerId: PropTypes.number.isRequired,
  /** The phone number of the pro */
  providerPhone: PropTypes.string,
  /** True if the button should be disabled */
  disabled: PropTypes.bool,
  /** The promotion associated with the service, if any */
  promotion: PromotionPropType,
  /** The overall index of the service in the list, used for tracking */
  index: PropTypes.number,
  /** The service group, used for tracking */
  group: ServiceGroupPropType,
  /** The text to use in the book button when online booking is enabled */
  bookText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
  ]),
  /** size */
  size: PropTypes.string,
  /** Additional style information for the button */
  style: PropTypes.object,
  /** Additional tracking params for the button */
  additionalTracking: PropTypes.object,
  /** Additional operations to do when the button is clicked */
  onClick: PropTypes.func,
  /** Custom testID for the book button */
  testID: PropTypes.string,
  /** matched service id from search passed to proprofile */
  highlightedServiceId: PropTypes.number,
};

BookButton.defaultProps = {
  isEditMode: false,
  isOnlineBookable: false,
  disabled: false,
  providerPhone: null,
  promotion: null,
  bookText: 'See Times',
  size: undefined,
  style: null,
  additionalTracking: null,
  onClick: null,
  group: null,
  testID: 'bookButton',
  index: -1,
  highlightedServiceId: null,
};

export default BookButton;
